import type { IComboBoxOption} from '@fluentui/react';
import { ComboBox, PrimaryButton, TextField } from '@fluentui/react';
import PageBase from '../../components/PageBase/PageBase';
import Strings from '../../localization/strings';
import './UserInfoPage.scss';
import React, { useContext, useEffect, useState } from 'react';
import getLocalizedCountries from 'localized-countries';
import localizedCountriesEn from 'localized-countries/data/en.json';
import localizedCountriesCs from 'localized-countries/data/cs.json';
import localizedCountriesSk from 'localized-countries/data/sk.json';
import { allCountries } from 'country-telephone-data';
import { FormContext } from '../../providers/FormProvider';
import PageTitle from '../../components/PageTitle/PageTitle';
import useValidatePreviousFormikOnMount from '../../helpers/hooks/useValidatePreviousFormikOnMount';
import LocalizationHelper from '../../localization/LocalizationHelper';
import { IntroPageNameInputs } from '../IntroPage/IntroPage';
import pageImg from '../../img/graphic-design/3-02.svg';

const LOCALIZED_COUNTRIES_DATA: Record<string, object> = { en: localizedCountriesEn, cs: localizedCountriesCs, sk: localizedCountriesSk };

export const loadCountryCallingCodeOptions = () => {
    const language = Strings.getLanguage();
    const localizedCountriesData = LOCALIZED_COUNTRIES_DATA[language] ?? localizedCountriesEn;
    const localizedCountries = getLocalizedCountries(localizedCountriesData).array();
    const localizedCountriesMap: { [code: string]: string } = {};
    localizedCountries.forEach((v) => {
        localizedCountriesMap[v.code] = v.label;
    });

    const options: IComboBoxOption[] = allCountries.map((country) => {
        const isoCountryCode = country.iso2.toUpperCase();

        return {
            key: isoCountryCode,
            text: `${localizedCountriesMap[isoCountryCode] ?? country.name} (+${country.dialCode})`,
            data: country.format?.startsWith('+') ? `+${country.dialCode}` : country.dialCode, // Simplified formatting just to add + if applicable
        };
    });

    return options;
};

const useShouldDisplayNameInputs = () => {
    const { introFormik, isLoggedViaMs } = useContext(FormContext);
    const [shouldDisplayNameInputs] = React.useState(isLoggedViaMs && (!introFormik.values.firstName || !introFormik.values.lastName));

    return shouldDisplayNameInputs;
};

const UserInfoPage = () => {
    const { userInfoFormik, introFormik, isLoggedViaMs, formFinalizationAction } = useContext(FormContext);
    const shouldDisplayNameInputs = useShouldDisplayNameInputs();
    // Once the trial creation (finalization) has been kicked off, the values on this page are
    // already captured and can no longer affect the trial. Lock the fields so it's clear they
    // are no longer modifiable when the user navigates back here.
    const isLocked = formFinalizationAction !== null;
    const [phoneOptions, setPhoneOptions] = useState<IComboBoxOption[]>([]);
    const myStrings = Strings.userInfoPage;

    useValidatePreviousFormikOnMount(introFormik, isLoggedViaMs && !!introFormik.values.email);

    useEffect(() => {
        const changePhoneDialCode = async (option?: IComboBoxOption) => {
            if (!option) {
                return;
            }

            await userInfoFormik.setFieldValue('phoneDialCode', option);

            if (!!userInfoFormik.values.phoneNumber) {
                // Is prefilled from MS
                const replaceValue = `+${option.data as string}`;
                const regex = new RegExp(`\\${replaceValue}+\\s?`);
                const newPhoneNumber = userInfoFormik.values.phoneNumber.replace(regex, "");
                await userInfoFormik.setFieldValue('phoneNumber', newPhoneNumber);
            }
        };

        const loadCountries = async () => {
            const options = loadCountryCallingCodeOptions();
            const sortedOptions = options.sort((a, b) => a.text.localeCompare(b.text));

            setPhoneOptions(sortedOptions);
            if (!userInfoFormik.values.phoneDialCode) {
                if (LocalizationHelper.isLanguageCs()) {
                    const csOption = sortedOptions.find((o) => o.key === 'CZ');
                    await changePhoneDialCode(csOption);
                } else if (LocalizationHelper.isLanguageSk()) {
                    const skOption = sortedOptions.find((o) => o.key === 'SK');
                    await changePhoneDialCode(skOption);
                } else {
                    const usOption = sortedOptions.find((o) => o.key === 'US');
                    await changePhoneDialCode(usOption);
                }
            }
        };

        if (phoneOptions.length === 0) {
            void loadCountries();
        }
    }, [userInfoFormik, phoneOptions]);

    const companyNameErrorMessage = userInfoFormik.touched.companyName ? userInfoFormik.errors.companyName : undefined;
    const phoneNumberErrorMessage = userInfoFormik.touched.phoneNumber ? userInfoFormik.errors.phoneNumber : undefined;
    const { nameDeclension } = introFormik.values;

    return (
        <PageBase imgSrc={pageImg} imgStyles={{ minWidth: "unset", maxWidth: "360px" }}>
            <form onSubmit={userInfoFormik.handleSubmit}>
                <PageTitle title={nameDeclension ? Strings.formatString(myStrings.niceToMeetYouName, nameDeclension) as string : myStrings.niceToMeetYou} />
                <div>
                    {shouldDisplayNameInputs && <IntroPageNameInputs disabled={isLocked} />}
                    <TextField
                        placeholder={myStrings.companyNamePlaceholder}
                        autoComplete="organization"
                        {...userInfoFormik.getFieldProps('companyName')}
                        errorMessage={companyNameErrorMessage}
                        className="user-info-page__company-input rounded"
                        maxLength={256}
                        disabled={isLocked}
                    />
                    <div className="user-info-page__phone-inputs">
                        <ComboBox options={phoneOptions} selectedKey={userInfoFormik.values.phoneDialCode?.key} onChange={(e, o) => void userInfoFormik.setFieldValue('phoneDialCode', o)} className="rounded" disabled={isLocked} />
                        <TextField
                            placeholder={myStrings.phoneNumberPlaceholder}
                            autoComplete="tel-national"
                            className="user-info-page__phone-input rounded"
                            {...userInfoFormik.getFieldProps('phoneNumber')}
                            errorMessage={phoneNumberErrorMessage}
                            maxLength={30} // Max is 15, but leave room for spaces etc.
                            disabled={isLocked}
                        />
                    </div>
                </div>
                <PrimaryButton className="page-base__next-btn rounded" type="submit" disabled={!userInfoFormik.isValid || (shouldDisplayNameInputs && !introFormik.isValid)}>
                    {Strings.next}
                </PrimaryButton>
            </form>
        </PageBase>
    );
};

export default UserInfoPage;
